/* ═══════════════════════════════════════════════════════════════
   The Designer — the TABLE half of the right-click menu
   (Phase 1 item 3, MASTERS_UNIFIED_PLAN, 2026-08-25).

   Right-clicking inside a table has to offer what Word offers, and
   the app already owns exactly one context-menu surface (`.dsg-menu`
   + `.dsg-menu-item`, used by the Designer's BlockMenu and the Spec
   Studio's SpecBlockMenu). So this is NOT a second menu primitive:
   it is a group of items both existing menus render when the caret
   sits in a table, which keeps every block-level action (delete,
   level, variant ops, export skip) exactly where it was.

   Every command is the framework's own — addRowBefore / addRowAfter /
   deleteRow / addColumnBefore / addColumnAfter / deleteColumn /
   mergeCells / splitCell / toggleHeaderRow / deleteTable. Only the
   two style groups (shading, Word-style borders) touch attrs the
   framework has no opinion about, via designer/tableCommands. The
   Borders group mirrors the Tables toolbox exactly (pen + target,
   T1.3) — menu/toolbox parity.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import {
  BORDER_PENS,
  BORDER_TARGETS,
  borderPen,
  blendToWhite,
  getBorderPen,
  MAX_TABLE_WIDTH_PCT,
  MIN_TABLE_WIDTH_PCT,
  setBorderPen,
  subscribeBorderPen,
  TABLE_NODE_NAME,
} from './tables';
import { ShadePicker } from './ShadePicker';
import {
  applyBorder,
  autofitColumns,
  distributeColumns,
  hasCellSelection,
  inTable,
  MAX_CELL_PADDING_PT,
  selectedCellShade,
  selectedCellVAlign,
  selectedRowMinHeight,
  setAllCellBorders,
  setCellPadding,
  setCellVAlign,
  setRowMinHeight,
  setTableJustify,
  setTableWidth,
  splitTableAtSelection,
  tableStyleAt,
} from './tableCommands';
import {
  ContextMenuCheckboxItem,
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '../components/ui/context-menu';
import { rem } from '../lib/rem';

/** A submenu level — Word-style grouping so the table's commands read as a
 *  short list. Always the kit ContextMenuSub (both hosts are Radix menus). */
function Sub({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger className="dsg-menu-item">
        {label}
        <span className="dsg-menu-key">▸</span>
      </ContextMenuSubTrigger>
      <ContextMenuSubContent className="dsg-menu" style={{ width: rem(248) }}>
        {children}
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}

interface Props {
  editor: Editor;
  canEdit: boolean;
  /** Close the host menu after a command runs. */
  onClose: () => void;
  /** The Spec Studio's right-click menu is built on the kit ContextMenu
   *  (Radix) — its ↑/↓ roving nav only reaches real Radix Item primitives,
   *  so these rows render as `ContextMenuItem`/`ContextMenuCheckboxItem`
   *  there (Modules R2 Phase 6, Josh 2026-08-27). The Designer's own
   *  `BlockMenu` is a hand-rolled `role="menu"` div, not Radix, so it keeps
   *  the plain buttons it always had — this prop defaults to false and that
   *  call site is untouched. */
  radixItems?: boolean;
  /** Spec Studio renders these rows top-level in a table: no leading divider
   *  or "Table" header row — the commands ARE the context. */
  bare?: boolean;
}

/** A structural table command — the SAME row either way, just wrapped in a
 *  Radix `ContextMenuItem` when the host menu is Radix so it joins arrow-key
 *  navigation. `keepOpen` is for the two rows that expand a sub-panel
 *  in place (Cell shading…/Cell borders…) rather than running a command —
 *  Radix closes the menu on select by default, so those two override it. */
function Row({
  radix,
  className,
  onClick,
  disabled,
  title,
  keepOpen,
  ariaHaspopup,
  ariaExpanded,
  children,
}: {
  radix: boolean;
  className: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  keepOpen?: boolean;
  ariaHaspopup?: boolean;
  ariaExpanded?: boolean;
  children: React.ReactNode;
}) {
  if (radix) {
    return (
      <ContextMenuItem
        className={className}
        disabled={disabled}
        title={title}
        aria-haspopup={ariaHaspopup}
        aria-expanded={ariaExpanded}
        onSelect={(e) => {
          if (keepOpen) e.preventDefault();
          onClick();
        }}
      >
        {children}
      </ContextMenuItem>
    );
  }
  return (
    <button
      type="button"
      role="menuitem"
      aria-haspopup={ariaHaspopup}
      aria-expanded={ariaExpanded}
      className={className}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}

/** A checkbox-style table row (Header row / Numbered under this level) —
 *  same two surfaces as `Row` above. */
function CheckRow({
  radix,
  checked,
  className,
  onClick,
  disabled,
  title,
  children,
}: {
  radix: boolean;
  checked: boolean;
  className: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}) {
  if (radix) {
    return (
      <ContextMenuCheckboxItem
        className={className}
        checked={checked}
        disabled={disabled}
        title={title}
        onCheckedChange={onClick}
      >
        {children}
      </ContextMenuCheckboxItem>
    );
  }
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      className={className}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}

/** One number, asked for in place — the same expand-under-the-row panel the
 *  shading and border pickers use, so the menu never hands a single number
 *  off to a dialog. Apply commits, Clear puts the setting back to "none". */
function NumberSub({
  label,
  unit,
  value,
  min,
  max,
  disabled,
  onApply,
  onClear,
}: {
  label: string;
  unit: string;
  value: number | null;
  min: number;
  max: number;
  disabled?: boolean;
  onApply: (n: number) => void;
  onClear?: () => void;
}) {
  const [draft, setDraft] = React.useState(value === null ? '' : String(value));
  const commit = () => {
    const n = Number(draft);
    if (Number.isFinite(n)) onApply(Math.min(Math.max(n, min), max));
  };
  return (
    <div className="dsg-menu-sub dsg-menu-num" role="group" aria-label={label}>
      <input
        type="number"
        className="dsg-menu-num-field"
        aria-label={label}
        min={min}
        max={max}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
      />
      <span className="dsg-menu-num-unit" aria-hidden="true">
        {unit}
      </span>
      <button
        type="button"
        role="menuitem"
        className="dsg-menu-item"
        onClick={commit}
        disabled={disabled}
      >
        Apply
      </button>
      {onClear && (
        <button
          type="button"
          role="menuitem"
          className="dsg-menu-item"
          onClick={() => {
            setDraft('');
            onClear();
          }}
          disabled={disabled}
        >
          Clear
        </button>
      )}
    </div>
  );
}

/** Vertical alignment of the content inside a cell — Word's three. */
const VALIGNS: ReadonlyArray<{ value: 'top' | 'middle' | 'bottom'; label: string }> = [
  { value: 'top', label: 'Top' },
  { value: 'middle', label: 'Middle' },
  { value: 'bottom', label: 'Bottom' },
];

/** Where the table sits across the page. */
const JUSTIFIES: ReadonlyArray<{ value: 'left' | 'center' | 'right'; label: string }> = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

/** The table node the selection is inside, with its position — the same
 *  depth-walk the table toolbar uses. */
function tableAt(editor: Editor): { pos: number; numbered: boolean } | null {
  const { $from } = editor.state.selection;
  for (let d = $from.depth; d >= 0; d--) {
    if ($from.node(d).type.name === TABLE_NODE_NAME) {
      return { pos: $from.before(d), numbered: !!$from.node(d).attrs.numbered };
    }
  }
  return null;
}

export function TableMenuSection({
  editor,
  canEdit,
  onClose,
  radixItems = false,
  bare = false,
}: Props) {
  const [showShades, setShowShades] = React.useState(false);
  const [showBorders, setShowBorders] = React.useState(false);
  const [showRowHeight, setShowRowHeight] = React.useState(false);
  const [showPadding, setShowPadding] = React.useState(false);
  const [showVAlign, setShowVAlign] = React.useState(false);
  const [showWidth, setShowWidth] = React.useState(false);
  const [showJustify, setShowJustify] = React.useState(false);

  // Shading and borders stay open while the user works, so the ticks they
  // show have to follow the document — the host menu does not re-render on
  // its own (its state is the right-click, not the editor).
  const [, force] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => {
    editor.on('transaction', force);
    return () => {
      editor.off('transaction', force);
    };
  }, [editor]);
  // The session's pen — the SAME one the Tables toolbox shows.
  const penId = React.useSyncExternalStore(subscribeBorderPen, getBorderPen, getBorderPen);

  if (!inTable(editor.state)) return null;
  const table = tableAt(editor);

  // Style pickers stay open (a user shades several cells in a row); every
  // structural command closes, like every other item in these menus.
  const run = (fn: () => void) => () => {
    fn();
    onClose();
  };
  const keep = (fn: () => void) => () => {
    fn();
    editor.commands.focus();
  };

  const shade = selectedCellShade(editor.state);
  const pen = borderPen(penId);
  const borderScope = hasCellSelection(editor.state) ? 'Selected cells' : 'Whole table';
  const headerOn = editor.isActive('tableHeader');
  const style = tableStyleAt(editor.state);
  const rowHeight = selectedRowMinHeight(editor.state);
  const valign = selectedCellVAlign(editor.state);

  return (
    <>
      {!bare && (
        <>
          <div className="dsg-menu-sep" role="separator" />
          <div className="dsg-menu-head" aria-hidden="true">
            Table
          </div>
        </>
      )}

      <Sub label="Insert">
        <Row
          radix={radixItems}
          className="dsg-menu-item"
          onClick={run(() => editor.chain().focus().addRowBefore().run())}
          disabled={!canEdit}
        >
          Row above
        </Row>
        <Row
          radix={radixItems}
          className="dsg-menu-item"
          onClick={run(() => editor.chain().focus().addRowAfter().run())}
          disabled={!canEdit}
        >
          Row below
        </Row>
        <Row
          radix={radixItems}
          className="dsg-menu-item"
          onClick={run(() => editor.chain().focus().addColumnBefore().run())}
          disabled={!canEdit}
        >
          Column left
        </Row>
        <Row
          radix={radixItems}
          className="dsg-menu-item"
          onClick={run(() => editor.chain().focus().addColumnAfter().run())}
          disabled={!canEdit}
        >
          Column right
        </Row>
      </Sub>

      <Sub label="Delete">
        <Row
          radix={radixItems}
          className="dsg-menu-item"
          onClick={run(() => editor.chain().focus().deleteRow().run())}
          disabled={!canEdit}
        >
          Row
        </Row>
        <Row
          radix={radixItems}
          className="dsg-menu-item"
          onClick={run(() => editor.chain().focus().deleteColumn().run())}
          disabled={!canEdit}
        >
          Column
        </Row>
        <div className="dsg-menu-sep" role="separator" />
        <Row
          radix={radixItems}
          className="dsg-menu-item is-danger"
          onClick={run(() => editor.chain().focus().deleteTable().run())}
          disabled={!canEdit}
          title="Delete the whole table — Ctrl+Z brings it back"
        >
          Table
        </Row>
      </Sub>

      <Row
        radix={radixItems}
        className="dsg-menu-item"
        onClick={run(() => editor.chain().focus().mergeCells().run())}
        disabled={!canEdit}
        title="Join the selected cells into one — drag across cells first"
      >
        Merge cells
      </Row>
      <Row
        radix={radixItems}
        className="dsg-menu-item"
        onClick={run(() => editor.chain().focus().splitCell().run())}
        disabled={!canEdit}
        title="Split a merged cell back apart"
      >
        Split cell
      </Row>

      <div className="dsg-menu-sep" role="separator" />

      {/* ── Cell style: shading ── */}
      <Row
        radix={radixItems}
        keepOpen
        ariaHaspopup
        ariaExpanded={showShades}
        className="dsg-menu-item"
        onClick={() => setShowShades((v) => !v)}
        disabled={!canEdit}
      >
        Cell shading…
        {shade && (
          <span
            className="dsg-menu-swatch"
            style={{ background: blendToWhite(shade, 100) }}
            aria-hidden="true"
          />
        )}
      </Row>
      {showShades && (
        <div className="dsg-menu-sub" role="group" aria-label="Cell shading">
          <ShadePicker
            editor={editor}
            disabled={!canEdit}
            itemRole="menuitemradio"
            afterApply={() => editor.commands.focus()}
          />
        </div>
      )}

      {/* ── Borders (T1.3): the pen, then where it goes — parity with the
             Tables toolbox's Borders group. ── */}
      <Row
        radix={radixItems}
        keepOpen
        ariaHaspopup
        ariaExpanded={showBorders}
        className="dsg-menu-item"
        onClick={() => setShowBorders((v) => !v)}
        disabled={!canEdit}
      >
        Borders…
        <span className="dsg-menu-key">{pen.label}</span>
      </Row>
      {showBorders && (
        <div className="dsg-menu-sub" role="group" aria-label="Borders">
          <div className="dsg-menu-head" id="dsg-menu-bdr-line" aria-hidden="true">
            Line
          </div>
          <div role="radiogroup" aria-labelledby="dsg-menu-bdr-line">
            {BORDER_PENS.map((p) => {
              const on = p.id === penId;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={on}
                  className={`dsg-menu-item${on ? ' is-current' : ''}`}
                  onClick={() => setBorderPen(p.id)}
                  disabled={!canEdit}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="dsg-menu-sep" role="separator" />
          {BORDER_TARGETS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="menuitem"
              className="dsg-menu-item"
              onClick={keep(() => applyBorder(editor, t.id, pen.line))}
              disabled={!canEdit}
              title={`${t.id === 'none' ? 'Remove the lines' : `${pen.label} line`} · ${borderScope}`}
            >
              {t.label}
            </button>
          ))}
          <div className="dsg-menu-sep" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="dsg-menu-item"
            onClick={keep(() => setAllCellBorders(editor, null))}
            disabled={!canEdit}
            title="Back to the lines the table itself draws"
          >
            Use the table&apos;s lines
          </button>
        </div>
      )}

      <div className="dsg-menu-sep" role="separator" />

      <Sub label="Layout">
        <Row
          radix={radixItems}
          keepOpen
          ariaHaspopup
          ariaExpanded={showRowHeight}
          className="dsg-menu-item"
          onClick={() => setShowRowHeight((v) => !v)}
          disabled={!canEdit}
          title="A smallest height for the selected rows — they still grow for their content"
        >
          Row height…
          {rowHeight !== null && <span className="dsg-menu-key">{rowHeight} pt</span>}
        </Row>
        {showRowHeight && (
          <NumberSub
            label="Minimum row height in points"
            unit="pt"
            value={rowHeight}
            min={1}
            max={720}
            disabled={!canEdit}
            onApply={(n) => keep(() => setRowMinHeight(editor, n))()}
            onClear={() => keep(() => setRowMinHeight(editor, null))()}
          />
        )}

        <Row
          radix={radixItems}
          keepOpen
          ariaHaspopup
          ariaExpanded={showPadding}
          className="dsg-menu-item"
          onClick={() => setShowPadding((v) => !v)}
          disabled={!canEdit}
          title="Space inside every cell, all four sides"
        >
          Cell padding…
          {style.cellPadding !== undefined && (
            <span className="dsg-menu-key">{style.cellPadding} pt</span>
          )}
        </Row>
        {showPadding && (
          <NumberSub
            label="Cell padding in points"
            unit="pt"
            value={style.cellPadding ?? null}
            min={0}
            max={MAX_CELL_PADDING_PT}
            disabled={!canEdit}
            onApply={(n) => keep(() => setCellPadding(editor, n))()}
            onClear={() => keep(() => setCellPadding(editor, null))()}
          />
        )}

        <Row
          radix={radixItems}
          keepOpen
          ariaHaspopup
          ariaExpanded={showVAlign}
          className="dsg-menu-item"
          onClick={() => setShowVAlign((v) => !v)}
          disabled={!canEdit}
          title="Where the text sits in the selected cells, top to bottom"
        >
          Vertical align…
        </Row>
        {showVAlign && (
          <div className="dsg-menu-sub" role="group" aria-label="Vertical align">
            {VALIGNS.map((v) => {
              const on = (valign || 'top') === v.value;
              return (
                <button
                  key={v.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={on}
                  className={`dsg-menu-item${on ? ' is-current' : ''}`}
                  onClick={keep(() => setCellVAlign(editor, v.value))}
                  disabled={!canEdit}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        )}
        <div className="dsg-menu-sep" role="separator" />
        <Row
          radix={radixItems}
          keepOpen
          ariaHaspopup
          ariaExpanded={showWidth}
          className="dsg-menu-item"
          onClick={() => setShowWidth((v) => !v)}
          disabled={!canEdit}
          title="How much of the text column this table fills"
        >
          Table width…
          <span className="dsg-menu-key">{style.width ?? MAX_TABLE_WIDTH_PCT}%</span>
        </Row>
        {showWidth && (
          <NumberSub
            label="Table width as a percentage of the page"
            unit="%"
            value={style.width ?? MAX_TABLE_WIDTH_PCT}
            min={MIN_TABLE_WIDTH_PCT}
            max={MAX_TABLE_WIDTH_PCT}
            disabled={!canEdit}
            onApply={(n) => keep(() => setTableWidth(editor, n))()}
          />
        )}

        <Row
          radix={radixItems}
          keepOpen
          ariaHaspopup
          ariaExpanded={showJustify}
          className="dsg-menu-item"
          onClick={() => setShowJustify((v) => !v)}
          disabled={!canEdit}
          title="Where the table sits across the page"
        >
          Table alignment…
        </Row>
        {showJustify && (
          <div className="dsg-menu-sub" role="group" aria-label="Table alignment">
            {JUSTIFIES.map((j) => {
              const on = (style.tableJustify || 'left') === j.value;
              return (
                <button
                  key={j.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={on}
                  className={`dsg-menu-item${on ? ' is-current' : ''}`}
                  onClick={keep(() => setTableJustify(editor, j.value))}
                  disabled={!canEdit}
                >
                  {j.label}
                </button>
              );
            })}
          </div>
        )}
        <Row
          radix={radixItems}
          className="dsg-menu-item"
          onClick={run(() => distributeColumns(editor))}
          disabled={!canEdit}
          title="Give every column the same share of the table"
        >
          Distribute columns evenly
        </Row>
        <Row
          radix={radixItems}
          className={`dsg-menu-item${style.autofit ? ' is-current' : ''}`}
          onClick={run(() => autofitColumns(editor))}
          disabled={!canEdit}
          title="Let every column size itself to what is in it"
        >
          Autofit contents
        </Row>
      </Sub>

      <Sub label="More">
        <CheckRow
          radix={radixItems}
          checked={headerOn}
          className={`dsg-menu-item${headerOn ? ' is-current' : ''}`}
          onClick={run(() => editor.chain().focus().toggleHeaderRow().run())}
          disabled={!canEdit}
          title="A header row repeats at the top of every printed page"
        >
          Header row
        </CheckRow>
        {table && (
          <CheckRow
            radix={radixItems}
            checked={table.numbered}
            className={`dsg-menu-item${table.numbered ? ' is-current' : ''}`}
            onClick={run(() => {
              editor.view.dispatch(
                editor.state.tr.setNodeAttribute(table.pos, 'numbered', !table.numbered)
              );
            })}
            disabled={!canEdit}
            title={
              table.numbered
                ? 'The table carries this level’s number — click to make it standalone'
                : 'Standalone: no number, sits at the margin — click to number it under this level'
            }
          >
            Numbered under this level
          </CheckRow>
        )}
        <Row
          radix={radixItems}
          className="dsg-menu-item"
          onClick={run(() => splitTableAtSelection(editor))}
          disabled={!canEdit}
          title="Break this table in two — the row you are in starts the second table"
        >
          Split table
        </Row>
      </Sub>
    </>
  );
}
